import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Heart, ShoppingBag, Minus, Plus, ChevronLeft, Image as ImgIcon } from "lucide-react";
import { shopApi, shopperAuth } from "../lib/shop-api";
import { money } from "../lib/api";
import { useCart } from "../lib/cart";

export default function ProductPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { add } = useCart();
  const [p, setP] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [sizePick, setSizePick] = useState(null);
  const [colorPick, setColorPick] = useState(null);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    shopApi.getProduct(slug)
      .then((d) => { setP(d); setImgIdx(0); setLoading(false); })
      .catch(() => { setLoading(false); toast.error("Producto no encontrado"); });
  }, [slug]);

  const variants = p?.variants || [];
  const hasVariants = variants.length > 0;
  const colors = useMemo(() => [...new Set(variants.map((v) => v.color).filter(Boolean))], [variants]);
  const sizes = useMemo(() => {
    const vs = colorPick ? variants.filter((v) => v.color === colorPick) : variants;
    return [...new Set(vs.map((v) => v.size).filter(Boolean))];
  }, [variants, colorPick]);

  const selectedVariant = useMemo(() => {
    if (!hasVariants) return null;
    return variants.find(
      (v) => (!v.size || v.size === sizePick) && (!v.color || v.color === colorPick)
    ) || null;
  }, [variants, sizePick, colorPick, hasVariants]);

  const availableQty = hasVariants
    ? selectedVariant ? selectedVariant.quantity : 0
    : p?.quantity_simple || 0;

  const soldOut = p && (p.total_stock || 0) === 0;

  const addToCart = () => {
    if (!p) return;
    if (hasVariants && !selectedVariant) { toast.error("Elige una variante"); return; }
    if (availableQty < qty) { toast.error(`Solo quedan ${availableQty}`); return; }
    add({
      product_id: p.id,
      variant_id: selectedVariant?.id || null,
      name: p.name,
      image: (p.images || [])[0],
      size: selectedVariant?.size || null,
      color: selectedVariant?.color || null,
      price: selectedVariant?.price_override || p.price,
      quantity: qty,
    });
    toast.success("Agregado al carrito");
  };

  const toggleWishlist = async () => {
    if (!shopperAuth.isAuthed()) {
      toast.error("Inicia sesión para guardar favoritos");
      nav("/shop/account?redirect=" + encodeURIComponent(`/shop/product/${slug}`));
      return;
    }
    try {
      const r = await shopApi.toggleWishlist(p.id);
      toast.success(r.wishlisted ? "Guardado en favoritos" : "Removido de favoritos");
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (loading) return <div className="min-h-screen bg-[#0A0908] flex items-center justify-center text-[#F5F0E6]/60">Cargando…</div>;
  if (!p) return <div className="min-h-screen bg-[#0A0908] flex items-center justify-center text-[#F5F0E6]/60">Producto no encontrado</div>;

  const imgs = p.images && p.images.length ? p.images : [null];

  return (
    <div className="min-h-screen bg-[#0A0908] text-[#F5F0E6]">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <Link to="/shop" className="inline-flex items-center gap-1 text-sm text-[#F5F0E6]/60 hover:text-[#C89B3C] mb-6" data-testid="pdp-back">
          <ChevronLeft size={16} /> Volver a la tienda
        </Link>

        <div className="grid md:grid-cols-2 gap-8 lg:gap-16">
          <div>
            <div className="aspect-[4/5] bg-[#120F0D] rounded-xl overflow-hidden" data-testid="pdp-main-image">
              {imgs[imgIdx] ? (
                <img src={imgs[imgIdx]} alt={p.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/20"><ImgIcon size={60} /></div>
              )}
            </div>
            {imgs.length > 1 && (
              <div className="mt-3 grid grid-cols-5 gap-2">
                {imgs.map((im, i) => (
                  <button key={i} onClick={() => setImgIdx(i)} className={`aspect-square overflow-hidden rounded-lg border ${i === imgIdx ? "border-[#C89B3C]" : "border-white/10"}`}>
                    {im ? <img src={im} alt="" className="w-full h-full object-cover" /> : null}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <p className="text-[11px] uppercase tracking-[0.4em] text-[#C89B3C] mb-2">{p.category}{p.brand ? ` · ${p.brand}` : ""}</p>
            <h1 className="text-3xl md:text-5xl mb-4" style={{ fontFamily: "'Playfair Display'", fontWeight: 700 }} data-testid="pdp-title">{p.name}</h1>

            <div className="flex items-baseline gap-3 mb-6">
              <span className="text-3xl font-bold" data-testid="pdp-price">{money(p.price)}</span>
              {p.compare_at_price && p.compare_at_price > p.price && (
                <span className="text-base line-through text-white/40">{money(p.compare_at_price)}</span>
              )}
              {soldOut && <span className="text-xs uppercase tracking-widest bg-white/10 px-2 py-1 rounded" data-testid="pdp-soldout">Agotado</span>}
            </div>

            {p.description && <p className="text-[#F5F0E6]/80 leading-relaxed mb-6 whitespace-pre-line">{p.description}</p>}

            {colors.length > 0 && (
              <div className="mb-5">
                <p className="text-xs uppercase tracking-wider text-[#F5F0E6]/60 mb-2">Color: <span className="text-[#F5F0E6]">{colorPick || "elige uno"}</span></p>
                <div className="flex flex-wrap gap-2" data-testid="pdp-colors">
                  {colors.map((c) => (
                    <button key={c} onClick={() => { setColorPick(c); setSizePick(null); }} className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${colorPick === c ? "border-[#C89B3C] bg-[#C89B3C]/10 text-[#C89B3C]" : "border-white/15 text-[#F5F0E6]/80 hover:border-white/40"}`} data-testid={`pdp-color-${c}`}>
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {sizes.length > 0 && (
              <div className="mb-5">
                <p className="text-xs uppercase tracking-wider text-[#F5F0E6]/60 mb-2">Talla: <span className="text-[#F5F0E6]">{sizePick || "elige una"}</span></p>
                <div className="flex flex-wrap gap-2" data-testid="pdp-sizes">
                  {sizes.map((s) => {
                    const match = variants.find((v) => v.size === s && (!colorPick || v.color === colorPick));
                    const out = !match || match.quantity === 0;
                    return (
                      <button
                        key={s}
                        disabled={out}
                        onClick={() => setSizePick(s)}
                        className={`min-w-[46px] px-3 py-2 text-sm rounded border transition-colors ${sizePick === s ? "border-[#C89B3C] bg-[#C89B3C]/10 text-[#C89B3C]" : out ? "border-white/10 text-white/30 line-through cursor-not-allowed" : "border-white/15 text-[#F5F0E6]/80 hover:border-white/40"}`}
                        data-testid={`pdp-size-${s}`}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {hasVariants && selectedVariant && (
              <p className="text-xs text-[#F5F0E6]/60 mb-4" data-testid="pdp-availability">
                {selectedVariant.quantity > 0
                  ? <>En stock: <span className="text-[#C89B3C] font-semibold">{selectedVariant.quantity}</span></>
                  : <span className="text-red-400 font-semibold">Agotado</span>}
              </p>
            )}
            {!hasVariants && (
              <p className="text-xs text-[#F5F0E6]/60 mb-4" data-testid="pdp-availability">
                {availableQty > 0 ? <>En stock: <span className="text-[#C89B3C] font-semibold">{availableQty}</span></> : <span className="text-red-400 font-semibold">Agotado</span>}
              </p>
            )}

            <div className="flex items-center gap-3 mb-6">
              <div className="inline-flex items-center border border-white/15 rounded-full">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-2 text-[#F5F0E6]/80" data-testid="pdp-qty-minus"><Minus size={14} /></button>
                <span className="w-8 text-center text-[#F5F0E6]" data-testid="pdp-qty">{qty}</span>
                <button onClick={() => setQty((q) => q + 1)} className="p-2 text-[#F5F0E6]/80" data-testid="pdp-qty-plus"><Plus size={14} /></button>
              </div>
              <button
                onClick={addToCart}
                disabled={soldOut || (hasVariants && (!selectedVariant || selectedVariant.quantity === 0))}
                className="flex-1 flex items-center justify-center gap-2 bg-[#C89B3C] hover:bg-[#E6BF5F] disabled:bg-white/10 disabled:text-white/40 text-[#0A0908] font-bold uppercase tracking-wider py-3 px-6 rounded-full transition-colors"
                data-testid="pdp-add-to-cart"
              >
                <ShoppingBag size={18} /> Agregar al carrito
              </button>
              <button onClick={toggleWishlist} className="p-3 border border-white/15 rounded-full text-[#F5F0E6]/80 hover:border-[#C89B3C] hover:text-[#C89B3C]" data-testid="pdp-wishlist" title="Guardar en favoritos">
                <Heart size={18} />
              </button>
            </div>

            <div className="border-t border-white/10 pt-5 space-y-2 text-sm text-[#F5F0E6]/70">
              <p>· Envío estándar $15 USD, GRATIS en compras sobre $150</p>
              <p>· Pickup gratis en Hollister, CA</p>
              <p>· Devoluciones hasta 15 días</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
